import { SAYT_MIN_QUERY_LENGTH } from '../../constants/search-bar';
import { type TypeaheadAction, type TypeaheadState } from './search-bar-reducer.types';
import { cursorIndexProp, isDataSection, isSuggestionsSection, isTermsSection } from './search-bar-selectors';

/**
 * Reducer to manage state for the search bar and typeahead menu.
 */
export function searchBarReducer(state: Readonly<TypeaheadState>, action: TypeaheadAction): TypeaheadState {
	switch (action.type) {
		case 'inputChanged':
			return typeaheadOpened({ ...state, value: action.value.trim(), displayValue: action.value, valueChanged: true });
		case 'locationChanged':
			return {
				...state,
				...typeaheadClosed(state),
				...clearData(),
				value: action.term.trim(),
				displayValue: action.term,
				valueChanged: false,
				currentSearch: action.term.trim()
			};
		case 'inputFocused':
			return inputFocused(state);
		case 'inputBlurred':
			return { ...state, inputFocused: false };
		case 'inputCleared':
			return inputFocused({ ...state, value: '', displayValue: '', valueChanged: false });
		case 'typeaheadOpened':
			return typeaheadOpened(state);
		case 'typeaheadClosed':
			return { ...state, ...typeaheadClosed(state) };
		case 'cursorMoved':
			return { ...state, ...cursorMoved(state, action) };
		case 'cursorReset':
			return { ...state, ...cursorReset() };
		case 'recentTermsLoaded':
			return {
				...state,
				recentTerms: action.recentTerms ?? [],
				recentTermsCursorIndex: -1,
				recentTermsRemove: action.recentTerms ?? [],
				recentTermsRemoveCursorIndex: -1
			};
		case 'suggestionsLoading':
			return { ...state, suggestionsLoading: true };
		case 'suggestionsLoaded':
			return suggestionsLoaded(state, action);
		case 'canRenderProducts':
			return {
				...state,
				renderProducts: action.renderProducts,
				// Move cursor out of products section if we can no longer render products.
				...(!action.renderProducts && state.cursorSection === 'suggestedProducts' ? { inputFocused: true, ...cursorReset() } : null)
			};
		default:
			return state;
	}
}

/**
 * Helper to reset the cursor to the search input and clear per-section cursor positions.
 */
const cursorReset = (): Partial<TypeaheadState> => ({
	cursorSection: 'input',
	recentTermsCursorIndex: -1,
	suggestedTermsCursorIndex: -1,
	suggestedProductsCursorIndex: -1,
	recentTermsRemoveCursorIndex: -1
});

/**
 * Helper to clear recent and suggested data.
 */
const clearData = (): Partial<TypeaheadState> => ({
	recentTerms: [],
	suggestedTerms: [],
	suggestedProducts: []
});

/**
 * Helper to open the typeahead and show the correct section (recent or suggestions).
 */
const typeaheadOpened = (state: Readonly<TypeaheadState>): TypeaheadState => {
	if (state.valueChanged && state.value.length >= SAYT_MIN_QUERY_LENGTH) {
		return { ...state, showResults: true, showRecent: false, showSuggestions: true };
	}
	return { ...state, showResults: true, showRecent: true, showSuggestions: false };
};

/**
 * Helper to close the typeahead and reset a bunch of stuff.
 */
const typeaheadClosed = (state: Readonly<TypeaheadState>): Partial<TypeaheadState> => {
	return {
		...cursorReset(),
		displayValue: state.value,
		showResults: false,
		showRecent: false,
		showSuggestions: false,
		valueChanged: false,
		inputFocused: false
	};
};

/**
 * Help to focus the search input and reset other values.
 */
const inputFocused = (state: Readonly<TypeaheadState>): TypeaheadState => {
	return {
		...typeaheadOpened(state),
		...cursorReset(),
		inputFocused: true
	};
};

/**
 * Helper to move the cursor section index, updating displayValue as needed.
 */
const cursorMoved = (
	state: Readonly<TypeaheadState>,
	action: Extract<TypeaheadAction, { type: 'cursorMoved' }>
): Partial<TypeaheadState> => {
	const { section, index } = action;
	const partialState: Partial<TypeaheadState> = { cursorSection: section };
	if (section === 'input') {
		Object.assign(partialState, cursorReset(), { inputFocused: true, displayValue: state.value });
	}
	if (isDataSection(section)) {
		partialState[cursorIndexProp(section)] = index;
	}
	if (isTermsSection(section)) {
		partialState.displayValue = state[section][index];
	}
	return partialState;
};

/**
 * Helper to set suggestions data and handle cursor focus.
 */
const suggestionsLoaded = (
	state: Readonly<TypeaheadState>,
	action: Extract<TypeaheadAction, { type: 'suggestionsLoaded' }>
): TypeaheadState => {
	const newState: TypeaheadState = {
		...state,
		suggestionsLoading: false,
		suggestedTerms: action.suggestedTerms ?? [],
		suggestedProducts: action.suggestedProducts ?? [],
		suggestedTermsCursorIndex: 0,
		suggestedProductsCursorIndex: 0
	};

	// Reset focus to input if we loaded new suggestions while already focused on suggestions.
	return isSuggestionsSection(state.cursorSection) ? inputFocused(newState) : newState;
};
